"use client";

import { useCallback, useEffect, useState } from "react";
import { Alert } from "./alert";
import { DropdownMenue } from "./dropdown-menue";
import { Knopf } from "./knopf";
import { Auswahl, Kontrollkaestchen, Textfeld } from "./formular";
import { initialen } from "./formular/auswahl-konfiguration";

/**
 * Teilen-Menü (PM Paket 3b, B4, Design-Guide `teilen-menue.md`): Knopf „Teilen“ öffnet eine Fläche mit Wer-kann-öffnen-Wahl, Link
 * erstellen und kopieren, Zugriffsliste („Zugriff heute“, mit „Einladen“ für Personen ohne Zugriff) und aktiven Freigaben mit
 * Widerruf. Der Baustein ist objekt-neutral: Daten und Schreibwege kommen als Adapter vom Aufrufer, der nur die BESTEHENDEN Actions
 * ruft (kein zweiter Schreibweg, r111). Er prüft keine Rechte; jede Zeile, die er zeigt, hat der Aufrufer bereits mit der Prüfung der
 * Action gefüllt (r109/r110). Fehler stehen als Satz im Menü (r105), nie als Toast und nie als Code; ein Wurf des Adapters ist ein
 * Fehlschlag (wiederholbar), eine Ablehnung liefert der Adapter als `{ ok: false, satz }`.
 *
 * Abweichung AW-025 gegenüber dem Katalogblock `share-menu-with-invite-panel`: Einladung per Mailadresse und Rolle „Admin“ entfallen;
 * die Rechte-Stufe je Person ist kein Auswahlfeld (sie folgt der Projektrolle, Anzeige „Vollzugriff“/„Nur Lesen“/„Kein Zugriff“);
 * statt „Send“ steht „Link erstellen“; Zusatzabschnitt „Aktive Freigaben“.
 * Abweichung AW-040 gegenüber der Vorschau b04: keine Personensuche mit „Teilen“-Knopf, kein „Hilfe zum Teilen“, keine Rechte-Auswahl je Person.
 */
export type TeilenWer = "members" | "named" | "org";
export interface TeilenPerson {
  id: string;
  name: string;
  rolle: string | null;
  inaktiv: boolean;
  zugriff: "voll" | "lesen" | "keiner";
}
export interface TeilenFreigabe {
  id: string;
  /** Fertiger Text („Nur Berechtigte · erstellt am 11.10.2026“). */
  text: string;
  /** Anbieten nie großzügiger als Erlauben (r110): „Widerrufen“ nur, wo der Aufrufer es mit derselben Prüfung wie die Action erlaubt. Standard: ja. */
  widerrufbar?: boolean;
}
export type TeilenErgebnis = { ok: true } | { ok: false; satz: string };
export type TeilenDaten = { personen: TeilenPerson[]; freigaben: TeilenFreigabe[] };

export interface TeilenAdapter {
  /** Laden beim Öffnen: Personen mit Zugriffsstufe und aktive Freigaben; eine Ablehnung liefert der Adapter als `{ ok: false, satz }`. */
  lade: () => Promise<TeilenDaten | { ok: false; satz: string }>;
  /** Link anlegen; bei Erfolg die Adresse. */
  erstelle: (wer: TeilenWer, benannt: string[]) => Promise<{ ok: true; url: string } | { ok: false; satz: string }>;
  einladen: (personId: string) => Promise<TeilenErgebnis>;
  widerrufe: (freigabeId: string) => Promise<TeilenErgebnis>;
}

export const TEILEN_SAETZE = {
  laden: "Die Freigaben konnten nicht geladen werden. Bitte erneut versuchen.",
  erstellen: "Der Link konnte nicht erstellt werden. Bitte erneut versuchen.",
  einladen: "Die Einladung konnte nicht gesendet werden. Bitte erneut versuchen.",
  widerrufen: "Die Freigabe konnte nicht widerrufen werden. Bitte erneut versuchen.",
} as const;

const WER_OPTIONEN = [
  { wert: "members", label: "Nur Berechtigte (Objekt-Mitglieder)" },
  { wert: "named", label: "Bestimmte Personen" },
  { wert: "org", label: "Jeder mit Link (HORN intern)" },
];
const ZUGRIFF_TEXT: Record<TeilenPerson["zugriff"], string> = { voll: "Vollzugriff", lesen: "Nur Lesen", keiner: "Kein Zugriff" };

const AUSLOESER =
  "py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground shadow-2xs hover:bg-layer-hover disabled:opacity-50 disabled:pointer-events-none focus:outline-hidden focus:bg-layer-focus";
const FLAECHE =
  "fixed top-0 start-0 z-70 bg-dropdown border border-dropdown-line rounded-lg shadow-md sm:shadow-xl divide-y divide-dropdown-divider";

export function TeilenMenue({ adapter, beschriftung = "Teilen", objektName }: { adapter: TeilenAdapter; beschriftung?: string; objektName: string }) {
  return (
    <DropdownMenue
      ausloeserKlasse={AUSLOESER}
      ausloeserLabel={`${beschriftung}: ${objektName}`}
      ausloeser={
        <>
          {/* Lucide „Share2“ als Inline-SVG */}
          <svg className="shrink-0 size-4" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="18" cy="5" r="3" />
            <circle cx="6" cy="12" r="3" />
            <circle cx="18" cy="19" r="3" />
            <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
            <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
          </svg>
          {beschriftung}
        </>
      }
      rechts
      flaecheKlasse={FLAECHE}
    >
      {() => <TeilenFlaeche adapter={adapter} />}
    </DropdownMenue>
  );
}

function TeilenFlaeche({ adapter }: { adapter: TeilenAdapter }) {
  const [daten, setDaten] = useState<{ personen: TeilenPerson[]; freigaben: TeilenFreigabe[] } | null>(null);
  const [wer, setWer] = useState<TeilenWer>("members");
  const [benannt, setBenannt] = useState<string[]>([]);
  const [url, setUrl] = useState<string | null>(null);
  const [kopiert, setKopiert] = useState<"nein" | "ja" | "fehlt">("nein");
  const [satz, setSatz] = useState<string | null>(null);
  const [belegt, setBelegt] = useState(false);
  const [eingeladen, setEingeladen] = useState<ReadonlySet<string>>(new Set());
  const [frage, setFrage] = useState<string | null>(null);

  const lade = useCallback(async () => {
    try {
      const r = await adapter.lade();
      if ("ok" in r) {
        setSatz(r.satz);
        setDaten({ personen: [], freigaben: [] });
      } else {
        setDaten(r);
      }
    } catch (fehler) {
      console.error("Teilen: Laden fehlgeschlagen", fehler);
      setSatz(TEILEN_SAETZE.laden);
      setDaten({ personen: [], freigaben: [] });
    }
  }, [adapter]);
  useEffect(() => {
    void lade();
  }, [lade]);

  const kopiere = async (adresse: string) => {
    try {
      await navigator.clipboard.writeText(new URL(adresse, window.location.origin).toString());
      setKopiert("ja");
    } catch {
      setKopiert("fehlt");
    }
  };

  const fuehreAus = async (lauf: () => Promise<void>, fehlschlag: string) => {
    if (belegt) return;
    setBelegt(true);
    setSatz(null);
    try {
      await lauf();
    } catch (fehler) {
      console.error("Teilen: Vorgang fehlgeschlagen", fehler);
      setSatz(fehlschlag);
    } finally {
      setBelegt(false);
    }
  };

  const erstellen = () =>
    fuehreAus(async () => {
      const r = await adapter.erstelle(wer, wer === "named" ? benannt : []);
      if (!r.ok) {
        setSatz(r.satz);
        return;
      }
      setUrl(r.url);
      await kopiere(r.url);
      await lade();
    }, TEILEN_SAETZE.erstellen);

  return (
    <>
      {/* Preline: ui-components/ui-components-sub-menus-selection/ui-components-cat-account-and-workspace-menus/share-menu-with-invite-panel */}
      <div className="w-80 sm:w-120">
        <div className="p-4">
          <span className="block mb-2 text-[13px] font-medium text-foreground">Wer kann öffnen?</span>
          <Auswahl id="teilen-wer" label="Wer kann öffnen?" labelVersteckt groesse="kompakt" value={wer} optionen={WER_OPTIONEN} onChange={(e) => setWer(e.target.value as TeilenWer)} />
          <p className="mt-2 text-xs text-muted-foreground-1">Empfänger sehen immer den aktuellen Stand, im Rahmen ihrer Berechtigung. Endet der Zugriff auf die Aufgabe, endet auch der Link.</p>
          {wer === "named" && daten && (
            <div className="mt-3 max-h-40 overflow-y-auto space-y-1" role="group" aria-label="Bestimmte Personen">
              {daten.personen.map((p) => (
                <Kontrollkaestchen
                  key={p.id}
                  label={`${p.name}${p.inaktiv ? " (inaktiv)" : ""}`}
                  checked={benannt.includes(p.id)}
                  onChange={(e) => setBenannt((alt) => (e.target.checked ? [...alt, p.id] : alt.filter((x) => x !== p.id)))}
                />
              ))}
              {daten.personen.length === 0 && <p className="text-sm text-muted-foreground-1">Keine Personen.</p>}
            </div>
          )}
          <div className="mt-3 flex items-center gap-x-2">
            <div className="grow min-w-0">
              <Textfeld id="teilen-link" label="Link" labelVersteckt groesse="kompakt" readOnly value={url ?? ""} placeholder="Noch nicht erstellt" onFocus={(e) => e.currentTarget.select()} />
            </div>
            <Knopf variante="primaer" laedt={belegt} onClick={() => void erstellen()}>
              Link erstellen
            </Knopf>
          </div>
          <div role="status" aria-live="polite" className="mt-1 empty:hidden">
            {kopiert === "ja" && <span className="text-xs text-muted-foreground-1">Link kopiert</span>}
            {kopiert === "fehlt" && <span className="text-xs text-muted-foreground-1">Kopieren nicht möglich, bitte den Link markieren.</span>}
          </div>
          {satz && (
            <div className="mt-3">
              <Alert ton="warnung">{satz}</Alert>
            </div>
          )}
        </div>

        <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
          <span className="block text-[13px] font-medium text-foreground">Zugriff heute</span>
          {daten === null && <p className="text-sm text-muted-foreground-1" role="status">Lädt …</p>}
          {daten?.personen.map((p) => (
            <div key={p.id} className="w-full flex items-center gap-x-3">
              <span className="flex shrink-0 justify-center items-center size-9.5 bg-layer border border-layer-line text-muted-foreground-1 text-sm font-semibold rounded-full shadow-2xs" aria-hidden="true">
                {initialen(p.name)}
              </span>
              <div className="grow min-w-0">
                <span className="block text-sm font-medium text-foreground truncate">
                  {p.name}
                  {p.inaktiv && <span className="text-xs font-normal text-muted-foreground-1"> (inaktiv)</span>}
                </span>
                <span className="block text-[13px] text-muted-foreground-1 truncate">{p.rolle ?? "—"}</span>
              </div>
              {p.zugriff === "keiner" && !p.inaktiv ? (
                eingeladen.has(p.id) ? (
                  <span className="text-xs text-muted-foreground-1">Eingeladen</span>
                ) : (
                  <Knopf
                    groesse="xs"
                    disabled={belegt}
                    onClick={() =>
                      void fuehreAus(async () => {
                        const r = await adapter.einladen(p.id);
                        if (r.ok) setEingeladen((alt) => new Set(alt).add(p.id));
                        else setSatz(r.satz);
                      }, TEILEN_SAETZE.einladen)
                    }
                  >
                    Einladen
                  </Knopf>
                )
              ) : (
                <span className="text-xs text-muted-foreground-1">{ZUGRIFF_TEXT[p.zugriff]}</span>
              )}
            </div>
          ))}
          {daten && daten.personen.length === 0 && <p className="text-sm text-muted-foreground-1">Keine Objekt-Mitglieder.</p>}
        </div>

        {daten && daten.freigaben.length > 0 && (
          <div className="p-4 space-y-2">
            <span className="block text-[13px] font-medium text-foreground">Aktive Freigaben</span>
            {daten.freigaben.map((f) => (
              <div key={f.id}>
                <div className="flex items-center gap-x-2">
                  <span className="grow min-w-0 text-sm text-foreground">{f.text}</span>
                  {f.widerrufbar !== false && (
                    <Knopf groesse="xs" disabled={belegt} onClick={() => setFrage(f.id)}>
                      Widerrufen
                    </Knopf>
                  )}
                </div>
                {frage === f.id && (
                  <div className="mt-2 rounded-lg border border-layer-line p-3">
                    <p className="text-sm text-foreground">Der Link funktioniert danach nicht mehr, Empfänger verlieren sofort den Zugriff.</p>
                    <div className="mt-2 flex gap-x-2">
                      <Knopf groesse="xs" onClick={() => setFrage(null)}>
                        Abbrechen
                      </Knopf>
                      <Knopf
                        groesse="xs"
                        variante="gefahr"
                        laedt={belegt}
                        onClick={() =>
                          void fuehreAus(async () => {
                            const r = await adapter.widerrufe(f.id);
                            setFrage(null);
                            if (r.ok) await lade();
                            else setSatz(r.satz);
                          }, TEILEN_SAETZE.widerrufen)
                        }
                      >
                        Widerrufen
                      </Knopf>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
